import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { DebouncedFunc, throttle } from 'lodash';
import React, { useEffect } from 'react';

import { apiFetch, ApiRequest, ApiResponse } from 'framework/api';
import { MobxManager } from 'framework/mobx-integration';
import { RemoteEventsManager } from 'core/remoteEventsManager';
import { EventType } from 'framework/entities/events';

export type DataHolderConfig<TData, TResponseData = TData, TExtra = undefined> = {
	request?: ApiRequest<TResponseData> | (() => ApiRequest<TResponseData>);
	converter?: (data: TResponseData) => TData;
	onLoaded?: (dataHolder: DataHolder<TData, TResponseData, TExtra>) => void;
	onDestroy?: (dataHolder: DataHolder<TData, TResponseData, TExtra>) => void;
	initialValue?: TData;
	// the events that reload it, as RemoteEventsManager subscribes to them
	subscriptions?: { eventType: EventType }[];
	// an event reloads it right away, then at most once in this many milliseconds
	reloadInterval?: number;
	resetDataBeforeLoading?: boolean;
	// what the data was asked for by, kept for whoever holds it
	extra?: TExtra;
};

export class DataHolder<TData, TResponseData = TData, TExtra = undefined> {
	loading: boolean;
	loaded: boolean;
	error: boolean;
	message: string;

	data: TData;

	mobx = new MobxManager();
	subscription: { unsubscribe: () => void };
	response: ApiResponse<TResponseData>;

	private reloadOnEvent: DebouncedFunc<() => void> | null = null;

	constructor(public config?: DataHolderConfig<TData, TResponseData, TExtra>) {
		this.config ??= {};
		this.data = this.config.initialValue;

		makeAutoObservable<DataHolder<TData, TResponseData, TExtra>, 'reloadOnEvent'>(this, { reloadOnEvent: false });

		this.mobx.reaction(
			() => this.getRequest(),
			(request) => {
				void this.loadData(request);
			},
			{
				fireImmediately: true,
			},
		);

		this.mobx.reaction(
			() => this.config.subscriptions,
			() => {
				this.subscription?.unsubscribe();
				this.reloadOnEvent?.cancel();

				if (this.config.subscriptions?.length > 0) {
					const interval = this.config.reloadInterval ?? 0;
					this.reloadOnEvent = throttle(() => void this.reload(), interval);
					this.subscription = RemoteEventsManager.subscribeCallback(
						toJS(this.config.subscriptions),
						this.reloadOnEvent,
					);
				}
			},
			{
				fireImmediately: true,
			},
		);
	}

	get extra(): TExtra {
		return this.config.extra;
	}

	getRequest = () => {
		return typeof this.config.request == 'function' ? this.config.request() : this.config.request;
	};

	loadData = async (request: ApiRequest<TResponseData>) => {
		if (this.config.resetDataBeforeLoading) {
			this.data = this.config.initialValue;
		}

		this.loaded = false;
		this.error = false;
		this.message = '';

		if (request == null) {
			this.config.onLoaded?.(this);
			return;
		}

		this.loading = true;

		this.response = await apiFetch(request);

		runInAction(() => {
			this.loading = false;
			if (!this.response.success) {
				this.error = true;
				this.message = this.response.message;
				this.data = this.config.initialValue;
			} else {
				this.loaded = true;
				this.data = this.config.converter
					? this.config.converter(this.response.data)
					: (this.response.data as unknown as TData);
			}

			this.config.onLoaded?.(this);
		});
	};

	reload = () => {
		return this.loadData(this.getRequest());
	};

	destroy() {
		this.mobx.destroy();
		this.reloadOnEvent?.cancel();
		this.subscription?.unsubscribe();
		this.config.onDestroy?.(this);
	}
}

export function useDataHolder<TData>(config: DataHolderConfig<TData>) {
	const [dataHolder] = React.useState(
		() =>
			new DataHolder<TData>({
				initialValue: config.initialValue,
				onLoaded: config.onLoaded,
				request: config.request,
				converter: config.converter,
			}),
	);

	useEffect(() => {
		Object.assign(dataHolder.config, config);
	}, [config.request, config.converter, config.onLoaded]);

	return dataHolder;
}
